import { StyleSheet } from 'aphrodite';
import {
	fadeOutZoomOut,
	slideInFromBottomNFadeNZoom,
	slideInFromLeftNFadeNZoom,
	slideInFromRightNFadeNZoom,
	slideInFromTopNFadeNZoom,
} from '../../styles/animations';
import {
	antiFlashWhite,
	borderColor,
	brandPrimary,
	darkGrayFontColor,
	destructive,
	inputBorderColor,
	titles,
} from '../../styles/colors';
import { shadowMd, shadowSm } from '../../styles/shadows';
import { THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	input: {
		boxSizing: 'border-box',
		display: 'flex',
		width: '100%',
		background: THEME.colors.background.light,
		border: `1px solid ${inputBorderColor}`,
		boxShadow: shadowSm,
		transition: 'color, box-shadow',
		color: titles,
		height: 34,
		padding: '0 12px',
		fontSize: '0.8125rem',
		lineHeight: 1.43,
		borderRadius: THEME.common.borderRadius,
		outline: 'none',
		':focus-visible': {
			borderColor: brandPrimary,
			outline: 'none',
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.6,
		},
		'::placeholder': {
			color: `${darkGrayFontColor}CC`,
		},
		':is([readonly])': {
			background: `${antiFlashWhite}CC`,
			cursor: 'not-allowed',
		},
		':is([aria-invalid="true"])': {
			borderColor: destructive,
			boxShadow: `${destructive}1A 0px 0px 0px 3px`,
		},
	},
	control: {
		position: 'relative',
	},
	clear: {
		position: 'absolute',
		cursor: 'pointer',
		top: '50%',
		right: 8,
		transform: 'translateY(-50%)',
		borderRadius: 4,
		opacity: 0.7,
		transition: 'opacity',
		background: 'transparent',
		border: 'none',
		padding: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		':hover': {
			opacity: 1,
		},
		':focus': {
			outline: 'none',
			boxShadow: `${brandPrimary} 0px 0px 0px 2px`,
		},
		':disabled': {
			pointerEvents: 'none',
		},
		':is([data-disabled])': {
			pointerEvents: 'none',
		},
		':has(*) svg': {
			width: 10,
			height: 10,
		},
	},
	icon: {
		height: 16,
		width: 16,
		opacity: 0.5,
		flexShrink: 0,
	},
	status: {
		padding: '6px 8px',
		fontSize: '0.875rem',
		color: darkGrayFontColor,
		':empty': {
			margin: 0,
			padding: 0,
		},
	},
	positioner: {
		zIndex: 50,
		outline: 'none',
	},
	popup: {
		paddingTop: 4,
		paddingBottom: 4,
		width: 'var(--anchor-width)',
		maxHeight: 'min(var(--available-height), 368px)',
		maxWidth: 'var(--available-width)',
		overflowY: 'auto',
		scrollPaddingTop: 8,
		scrollPaddingBottom: 8,
		overscrollBehavior: 'contain',
		background: THEME.colors.background.light,
		borderRadius: 8,
		border: `1px solid ${borderColor}`,
		color: titles,
		boxShadow: shadowMd,
		transformOrigin: 'var(--transform-origin)',
		animationDuration: '100ms',
		animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		animationFillMode: 'forwards',
		':is([data-closed])': {
			animationName: fadeOutZoomOut,
			overflow: 'hidden',
		},
		':is([data-open][data-side="bottom"])': {
			animationName: slideInFromTopNFadeNZoom(),
		},
		':is([data-open][data-side="top"])': {
			animationName: slideInFromBottomNFadeNZoom(),
		},
		':is([data-open][data-side="left"])': {
			animationName: slideInFromRightNFadeNZoom(),
		},
		':is([data-open][data-side="right"])': {
			animationName: slideInFromLeftNFadeNZoom(),
		},
	},
	list: {
		gap: 2,
		display: 'flex',
		flexDirection: 'column',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	item: {
		color: titles,
		position: 'relative',
		display: 'flex',
		cursor: 'default',
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 6,
		padding: '8px 16px',
		fontSize: '0.875rem',
		outline: 'none',
		transitionProperty: 'color, background-color, border-color',
		transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		transitionDuration: '150ms',
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':has(*) svg': {
			pointerEvents: 'none',
		},
		':is([data-highlighted])': {
			position: 'relative',
			zIndex: 0,
			color: titles,
		},
		':is([data-highlighted]):before': {
			content: '""',
			position: 'absolute',
			left: 4,
			right: 4,
			top: 0,
			bottom: 0,
			zIndex: -1,
			borderRadius: 4,
			background: antiFlashWhite,
		},
	},
	groupLabel: {
		padding: '6px 8px',
		fontSize: '0.75rem',
		color: darkGrayFontColor,
		fontWeight: 'bold',
	},
	empty: {
		padding: '6px 8px',
		fontSize: '0.875rem',
		color: darkGrayFontColor,
		':empty': {
			margin: 0,
			padding: 0,
		},
	},
	separator: {
		margin: '6px 0',
		height: 1,
		background: antiFlashWhite,
	},
});
